import { motion } from "framer-motion";
import { useMemo } from "react";

const GameGrid = ({
  movieData,
  guesses,
  currentGuess,
  feedbacks,
  shakeRow,
  bounceRow,
  maxAttempts,
}) => {
  // Build the grid display
  const rows = useMemo(() => {
    if (!movieData) return [];
    
    const result = [];
    const titleLength = movieData.title_length;
    const spacePositions = new Set(movieData.space_positions);
    
    for (let rowIndex = 0; rowIndex < maxAttempts; rowIndex++) {
      const row = [];
      
      if (rowIndex < guesses.length) {
        // Completed guess row
        const guess = guesses[rowIndex];
        const feedback = feedbacks[rowIndex];
        
        for (let i = 0; i < titleLength; i++) {
          row.push({
            letter: guess[i] || "",
            state: feedback[i] || "empty",
            isSpace: guess[i] === " ",
          });
        }
      } else if (rowIndex === guesses.length) {
        // Current input row
        let guessIndex = 0;
        for (let i = 0; i < titleLength; i++) {
          if (spacePositions.has(i)) {
            row.push({
              letter: " ",
              state: "empty",
              isSpace: true,
            });
          } else {
            row.push({
              letter: currentGuess[guessIndex] || "",
              state: "empty",
              isSpace: false,
            });
            guessIndex++;
          }
        }
      } else {
        // Empty future rows
        for (let i = 0; i < titleLength; i++) {
          row.push({
            letter: "",
            state: "empty",
            isSpace: spacePositions.has(i),
          });
        }
      }
      
      result.push(row);
    }
    
    return result;
  }, [movieData, guesses, currentGuess, feedbacks, maxAttempts]);

  if (!movieData) return null;

  // Calculate tile size based on title length
  const titleLength = movieData.title_length;
  let tileSize = "w-12 h-12 text-2xl";
  let gap = "gap-1.5";
  
  if (titleLength > 15) {
    tileSize = "w-8 h-8 text-lg";
    gap = "gap-1";
  } else if (titleLength > 10) {
    tileSize = "w-10 h-10 text-xl";
    gap = "gap-1";
  }

  return (
    <div className="flex flex-col items-center gap-2" data-testid="game-grid">
      {rows.map((row, rowIndex) => (
        <motion.div
          key={rowIndex}
          className={`flex ${gap} justify-center`}
          animate={
            shakeRow === rowIndex
              ? { x: [0, -5, 5, -5, 5, 0] }
              : bounceRow === rowIndex
              ? { y: [0, -10, 0] }
              : {}
          }
          transition={{ duration: 0.4 }}
          data-testid={`grid-row-${rowIndex}`}
        >
          {row.map((cell, cellIndex) => (
            <Tile
              key={`${rowIndex}-${cellIndex}-${cell.state}`}
              letter={cell.letter}
              state={cell.state}
              isSpace={cell.isSpace}
              tileSize={tileSize}
              index={cellIndex}
            />
          ))}
        </motion.div>
      ))}
    </div>
  );
};

const Tile = ({ letter, state, isSpace, tileSize, index }) => {
  if (isSpace) {
    return (
      <div
        className={`${tileSize} flex items-center justify-center`}
        data-testid="tile-space"
      >
        <div className="w-1 h-1 bg-white/20 rounded-full" />
      </div>
    );
  }

  // Color mapping
  const colors = {
    empty: { bg: "#121212", border: "#3A3A3C" },
    correct: { bg: "#538D4E", border: "#538D4E" },
    present: { bg: "#B59F3B", border: "#B59F3B" },
    absent: { bg: "#3A3A3C", border: "#3A3A3C" },
  };

  const color = colors[state] || colors.empty;

  return (
    <motion.div
      className={`${tileSize} rounded-md font-game font-bold uppercase flex items-center justify-center text-white border-2`}
      initial={{ scale: 1 }}
      animate={{ 
        scale: letter && state === "empty" ? [1, 1.05, 1] : 1,
        backgroundColor: color.bg,
        borderColor: color.border,
      }}
      transition={{ 
        duration: 0.15,
        backgroundColor: { duration: 0.3, delay: index * 0.1 },
        borderColor: { duration: 0.3, delay: index * 0.1 },
      }}
      data-testid={`tile-${index}`}
      data-state={state}
    >
      {letter}
    </motion.div>
  );
};

export default GameGrid;
